// Phase 43 — useProvenance hook (Plan 43-04, CANVAS-03 / D-05 / D-07).
// Per-field provenance read/write over react-hook-form state. See
// 43-RESEARCH.md "Research Area 1" + "Code Example 2" and 43-PATTERNS.md §27.
//
// Critical contracts (enforced by tests + acceptance grep):
// - Writes use the dotted path `_provenance.${fieldName}` — NEVER the bare
//   `_provenance` map (Pitfall #5). Replacing the full map would trash
//   unrelated fields and defeat RHF's per-field subscription.
// - `watch` subscribes to the dotted path too (Pitfall #1) — a bare
//   `watch('_provenance')` would re-render every chip on every sibling edit.
// - tintClass resolves to 'tint-ai' | 'tint-ai-low' | 'tint-conflict' | ''
//   consumed by FieldTintWrapper against the canvas.css classes landed in
//   Plan 43-01.
// - Dev-only conflict seed (CONFLICT_SEED_ENABLED): build-time-inlined by
//   Vite. In prod, `.env.production` must OMIT VITE_CANVAS_CONFLICT_SEED_ENABLED
//   so terser tree-shakes the seed branch — CI gate in Plan 43-07. Never
//   read from a runtime source.

import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { CanvasReviewContext } from '../CanvasReview';
import { engineFormPath } from '../lib/engineProvenanceKey';
import { sortByAuthority } from '../lib/sourceAuthority';
import type {
  BoatFormData,
  ProvenanceEntry,
} from '@/modules/inventory/types/boat.types';

export interface ProvenanceState {
  entry: ProvenanceEntry | ProvenanceEntry[] | null;
  // Phase 45.5 (UI-SPEC §"AI-filled field states") — the 4-state field contract.
  // 'tint-ai'      = AI-filled high-confidence (violet border)
  // 'tint-ai-low'  = AI-filled low-confidence / scanned (amber "please check")
  // 'tint-conflict'= sources disagree (amber, ConflictChooser expands)
  // ''             = user-entered (edited → border DROPS, neutral) OR empty
  // NOTE: 'tint-ai-edited' is GONE — an edited field is NEUTRAL per the LOCKED
  // contract (editing is the only accept gesture; the AI border drops).
  tintClass: 'tint-ai' | 'tint-ai-low' | 'tint-conflict' | '';
  isConflict: boolean;
  // True when the single entry is a low-confidence AI fill (drives the amber
  // "please check" chip text). False for high-confidence, conflict, or edited.
  isLowConfidence: boolean;
  // True once the dealer has edited an AI-filled field (chip → "✓ Edited by you").
  isEdited: boolean;
  // #1317 — the source the dealer picked in the ConflictChooser (the entry whose
  // state is 'confirmed'), and its index within sortByAuthority(entries) — the
  // order every chooser renders. null when nothing is picked or not a conflict.
  pickedIndex: number | null;
  pickedEntry: ProvenanceEntry | null;
}

export interface UseProvenanceOptions {
  // #1317 — called after a chooser pick has written the value and marked the
  // entry confirmed, so the server confirms the row. Defaults to the canvas
  // review context's `pickSource` (absent outside the canvas). `restore` puts
  // back the field value and `_provenance` the pick replaced; call it when the
  // server does not confirm the pick (failed or skipped apply).
  onPick?: (entry: ProvenanceEntry, restore: () => void) => void;
}

// UI-SPEC §"AI-filled field states": a field_confidence of medium/low renders
// amber "please check". On the 0-1 provenance scale, ≥ this floor is "high"
// (violet); below is "low" (amber). 0.7 mirrors the cascade-gate threshold.
export const AI_CONFIDENCE_FLOOR = 0.7;

// 1-2 fields get a synthetic multi-source conflict in dev/staging so
// ConflictChooser can be exercised for QA before real conflicts land in a
// later phase. Module-local by design — do not export.
const SEED_FIELDS = new Set(['manufacturer', 'hullMaterial']);

function isSeedEnabled(): boolean {
  // Read at call-time (not module init) so vi.stubEnv() works under test.
  return (
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    import.meta.env.VITE_CANVAS_CONFLICT_SEED_ENABLED === 'true'
  );
}

function getSeedEntries(fieldName: string): ProvenanceEntry[] | null {
  if (!isSeedEnabled()) return null;
  if (!SEED_FIELDS.has(fieldName)) return null;
  // Phase 45.5 (D-01): 'URL' source retired (INGEST-02 cut). The dev/staging
  // conflict seed now uses two real packet sources (Boat COO vs Invoice) so
  // ConflictChooser is exercised against the doc-typed authority order.
  return [
    {
      source: 'invoice',
      value: 'Bennington',
      confidence: 0.82,
      reasoning: 'Read from dealer invoice (SEED)',
      updatedAt: '2026-04-22T00:00:00Z',
    },
    {
      source: 'COO-boat',
      value: 'Bennington Marine',
      confidence: 0.91,
      reasoning: 'Read from boat Certificate of Origin (SEED)',
      updatedAt: '2026-04-22T00:00:00Z',
    },
  ];
}

export function useProvenance(fieldName: string, options: UseProvenanceOptions = {}): {
  state: ProvenanceState;
  set: (entry: ProvenanceEntry) => void;
  setMany: (updates: Record<string, ProvenanceEntry>) => void;
  clear: () => void;
  selectSource: (chosen: ProvenanceEntry) => void;
  markEdited: () => void;
} {
  const { watch, setValue, getValues } = useFormContext<BoatFormData>();
  const review = useContext(CanvasReviewContext);
  const pickSource = review?.pickSource;
  const onPick =
    options.onPick ??
    (pickSource ? (entry: ProvenanceEntry, restore: () => void) => pickSource(fieldName, entry, restore) : undefined);

  // Subscribe to THIS field's provenance only (Pitfall #1).
  // react-hook-form supports dotted paths in watch() / setValue().
  const watched = watch(`_provenance.${fieldName}` as const);

  const seed = getSeedEntries(fieldName);
  const raw = seed ?? watched ?? null;

  const isConflict = Array.isArray(raw);
  const single = !isConflict && raw ? (raw as ProvenanceEntry) : null;

  // The 4-state contract (UI-SPEC §"AI-filled field states"), in priority order:
  //   edited/confirmed/rejected → NEUTRAL ('') — dealer-owned / accepted
  //   conflict    → amber, ConflictChooser
  //   auto_applied → violet (merge said green — confidence-independent)
  //   suggested    → amber (merge said amber)
  //   stream-only (no state) → confidence heuristic fallback
  //   empty/manual→ neutral ('')
  //
  // Phase 45.5.2 sub-plan 5: state is authoritative when present (written by
  // the merge engine). Confidence heuristic is only used for pre-merge
  // stream-only entries that have no state yet.
  const isEdited = Boolean(single?.userEdited) || single?.state === 'edited';
  const fieldState = single?.state;

  // #1317 — a picked source resolves the conflict but keeps the chooser up.
  const sorted = isConflict ? sortByAuthority(raw as ProvenanceEntry[]) : [];
  const pickedAt = sorted.findIndex((entry) => entry.state === 'confirmed');
  const pickedIndex = pickedAt === -1 ? null : pickedAt;
  const pickedEntry = pickedIndex === null ? null : sorted[pickedIndex];

  let tintClass: ProvenanceState['tintClass'] = '';
  let isLowConfidence = false;

  if (isEdited || fieldState === 'confirmed' || fieldState === 'rejected' || pickedEntry) {
    tintClass = ''; // dealer-owned / accepted → neutral
  } else if (isConflict) {
    tintClass = 'tint-conflict';
  } else if (fieldState === 'auto_applied') {
    tintClass = 'tint-ai'; // merge said green — confidence-independent
  } else if (fieldState === 'suggested') {
    tintClass = 'tint-ai-low'; // merge said amber
    isLowConfidence = true;
  } else if (single && single.source !== 'manual') {
    // Stream-only (pre-merge) entry — no state yet → confidence heuristic.
    isLowConfidence = single.confidence < AI_CONFIDENCE_FLOOR;
    tintClass = isLowConfidence ? 'tint-ai-low' : 'tint-ai';
  }

  // Per-field writes only (Pitfall #5). NEVER setValue('_provenance', ...).
  const set = (entry: ProvenanceEntry) =>
    setValue(`_provenance.${fieldName}` as const, entry, { shouldDirty: true });

  const setMany = (updates: Record<string, ProvenanceEntry>) => {
    for (const [k, v] of Object.entries(updates)) {
      setValue(`_provenance.${k}` as const, v, { shouldDirty: true });
    }
  };

  const clear = () =>
    setValue(`_provenance.${fieldName}` as const, undefined as never, {
      shouldDirty: true,
    });

  const selectSource = (chosen: ProvenanceEntry) => {
    // #1182 — picking a source writes its value into the field (it used to only
    // change the tint, leaving the other source's value in place). The canvas's
    // changes-only save then sends it as the dealer's choice.
    // #1307 — an engine key writes the engine row's form field.
    const target = fieldName.startsWith('engine#') ? engineFormPath(fieldName) : fieldName;
    // #1317 — what the pick replaces, so a pick the server does not confirm is undone.
    const previousValue = target !== null ? getValues(target as never) : undefined;
    const previousProvenance = getValues(`_provenance.${fieldName}` as const);
    const restore = () => {
      if (target !== null) setValue(target as never, previousValue as never, { shouldDirty: true });
      setValue(`_provenance.${fieldName}` as const, previousProvenance, { shouldDirty: true });
    };
    if (target !== null) {
      setValue(target as never, chosen.value as never, { shouldDirty: true });
    }
    // #1317 — picking a source attributes the field to it (replaces the D-13
    // collapse to "✓ Edited by you"): the array stays so the chooser stays up,
    // the chosen entry is marked confirmed and any earlier pick is demoted.
    // The server confirms the row through `onPick`; its refetch then carries
    // the same confirmed entry back.
    if (Array.isArray(raw)) {
      const isChosen = (entry: ProvenanceEntry) =>
        chosen.id !== undefined ? entry.id === chosen.id : entry === chosen;
      const next = raw.map((entry): ProvenanceEntry => {
        if (isChosen(entry)) return { ...entry, state: 'confirmed' };
        if (entry.state === 'confirmed') return { ...entry, state: 'suggested' };
        return entry;
      });
      setValue(`_provenance.${fieldName}` as const, next, { shouldDirty: true });
    } else {
      setValue(`_provenance.${fieldName}` as const, { ...chosen, state: 'confirmed' }, { shouldDirty: true });
    }
    if (chosen.id !== undefined) onPick?.(chosen, restore);
  };

  // Phase 45.5 (D-13) — editing a field is the ONLY accept gesture. Flip the
  // field to user-entered: keep the existing source/value but mark userEdited
  // so the chip shows "✓ Edited by you", the border drops to neutral, and a
  // re-extract will NOT clobber it. No-op for an empty/manual field (no entry).
  const markEdited = () => {
    if (!raw) return;
    // For a conflict array, collapse to the picked entry (#1317) or else the
    // highest-confidence entry as the resolved value; otherwise keep the single entry.
    const base: ProvenanceEntry = isConflict
      ? (pickedEntry ?? [...(raw as ProvenanceEntry[])].sort((a, b) => b.confidence - a.confidence)[0])
      : (raw as ProvenanceEntry);
    if (base.userEdited) return; // already edited — idempotent
    setValue(
      `_provenance.${fieldName}` as const,
      { ...base, userEdited: true, updatedAt: new Date().toISOString() },
      { shouldDirty: true },
    );
  };

  return {
    state: { entry: raw ?? null, tintClass, isConflict, isLowConfidence, isEdited, pickedIndex, pickedEntry },
    set,
    setMany,
    clear,
    selectSource,
    markEdited,
  };
}
